import { useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { validateTimeRange, calculateDurationSeconds } from "@/utils/timeCalculations";

interface ManualEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManualEntryDialog({ open, onOpenChange }: ManualEntryDialogProps) {
  const today = format(new Date(), "yyyy-MM-dd");
  const [manualDate, setManualDate] = useState(today);
  const [startTime, setStartTime] = useState(format(new Date(), "HH:mm"));
  const [endTime, setEndTime] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const resetForm = () => {
    setManualDate(format(new Date(), "yyyy-MM-dd"));
    setStartTime(format(new Date(), "HH:mm"));
    setEndTime("");
    setDescription("");
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const handleManualEntry = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast({
        title: "Fehler",
        description: "Sie müssen angemeldet sein, um Zeiteinträge hinzuzufügen",
        variant: "destructive",
      });
      return;
    }

    const validationError = validateTimeRange(manualDate, startTime, endTime);
    if (validationError) {
      toast({ title: "Fehler", description: validationError, variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const startDateTime = new Date(`${manualDate}T${startTime}`);
      const endDateTime = new Date(`${manualDate}T${endTime}`);
      const duration = calculateDurationSeconds(startDateTime.toISOString(), endDateTime.toISOString());

      const { error } = await supabase.from("time_entries").insert({
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        duration,
        description,
        user_id: user.id,
      });

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["time-entries"] });
      queryClient.invalidateQueries({ queryKey: ["available-months"] });
      handleOpenChange(false);
      toast({ title: "Erfolg", description: "Zeiteintrag wurde hinzugefügt" });
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Zeiteintrag konnte nicht hinzugefügt werden",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Zeiteintrag hinzufügen</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleManualEntry}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="date">Datum</Label>
              <Input
                id="date"
                type="date"
                value={manualDate}
                onChange={(e) => setManualDate(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="start-time">Startzeit</Label>
              <Input
                id="start-time"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="end-time">Endzeit</Label>
              <Input
                id="end-time"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="manual-description">Beschreibung</Label>
              <Textarea
                id="manual-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Woran haben Sie gearbeitet?"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Wird gespeichert..." : "Eintrag hinzufügen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
